/**
 * WindowsContext - State management for the widget-based window system
 *
 * Handles:
 * - Loading/saving windows config to localStorage
 * - Server-side persistence with desktop/mobile discriminator
 * - CRUD operations on windows and widgets
 * - Layout persistence
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useState, useRef, useMemo, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import type {
  Window,
  WindowsConfig,
  Widget,
  WidgetLayout,
  WidgetType,
  WidgetConfigMap,
} from '../types/widgets';
import { createWindow, createWidget, DEFAULT_WIDGET_SIZES } from '../types/widgets';
import { authService } from '../services/auth';

// ============================================================================
// Device detection (reactive via matchMedia)
// ============================================================================

const MOBILE_QUERY = '(max-width: 767px)';

function getDeviceType(): 'desktop' | 'mobile' {
  return window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop';
}

function subscribeToDeviceType(callback: () => void): () => void {
  const mql = window.matchMedia(MOBILE_QUERY);
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
}

function useReactiveDeviceType(): 'desktop' | 'mobile' {
  return useSyncExternalStore(subscribeToDeviceType, getDeviceType, () => 'desktop');
}

// ============================================================================
// Storage
// ============================================================================

const STORAGE_KEY = 'windows_config';

function loadFromStorage(): WindowsConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return sanitizeConfig(JSON.parse(stored));
    }
  } catch (e) {
    console.error('Failed to load windows config:', e);
  }
  return null;
}

function saveToStorage(config: WindowsConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save windows config:', e);
  }
}

// ============================================================================
// Config Sanitization — strip unknown/removed widget types (e.g. 'projects')
// ============================================================================

const VALID_WIDGET_TYPES = new Set<string>(Object.keys(DEFAULT_WIDGET_SIZES));

function sanitizeConfig(config: WindowsConfig): WindowsConfig {
  let changed = false;
  const windows = config.windows.map((window) => {
    const validWidgets = window.widgets.filter((w) => {
      if (VALID_WIDGET_TYPES.has(w.type)) return true;
      changed = true;
      return false;
    });
    if (validWidgets.length === window.widgets.length) return window;
    const validIds = new Set(validWidgets.map((w) => w.id));
    return {
      ...window,
      widgets: validWidgets,
      layouts: window.layouts.filter((l) => validIds.has(l.i)),
    };
  });
  return changed ? { ...config, windows } : config;
}

// ============================================================================
// Default Configuration
// ============================================================================

function createDefaultConfig(): WindowsConfig {
  const now = new Date().toISOString();

  // ── Day Window (default, first) ──────────────────────────────────────
  // Top row: schedule + tasks + AI chat side-by-side (all visible above fold)
  // Bottom row: journal + activity feed + pomodoro
  const dayWidgets: Widget[] = [
    createWidget('day-schedule', { showEvents: true, showTasks: true }),
    createWidget('task-list', { title: "Today's Focus", dateFilter: 'today' }),
    createWidget('ai-chat', {}),
    createWidget('daily-journal', { showTasks: true, showMood: true }),
    createWidget('activity-feed', {
      title: 'Day Log',
      showTaskCreations: true,
      showTaskCompletions: true,
      showMoodLogs: true,
      showJournalEntries: true,
    }),
    createWidget('pomodoro', {}),
  ];

  const dayWindow: Window = {
    id: crypto.randomUUID(),
    name: 'Day',
    icon: 'sun',
    widgets: dayWidgets,
    layouts: [
      { i: dayWidgets[0].id, x: 0, y: 0, w: 4, h: 7, minW: 3, minH: 4 },
      { i: dayWidgets[1].id, x: 4, y: 0, w: 4, h: 7, minW: 2, minH: 3 },
      { i: dayWidgets[2].id, x: 8, y: 0, w: 4, h: 7, minW: 2, minH: 3 },
      { i: dayWidgets[3].id, x: 0, y: 7, w: 4, h: 6, minW: 3, minH: 4 },
      { i: dayWidgets[4].id, x: 4, y: 7, w: 4, h: 6, minW: 2, minH: 3 },
      { i: dayWidgets[5].id, x: 8, y: 7, w: 4, h: 6, minW: 2, minH: 3 },
    ],
    createdAt: now,
    updatedAt: now,
  };

  // ── Week Window ──────────────────────────────────────────────────────
  // Top row: two task lists + AI chat
  // Bottom row: weekly review + calendar + habit tracker
  const weekWidgets: Widget[] = [
    createWidget('task-list', { title: 'This Week', dateFilter: 'this-week' }),
    createWidget('task-list', { title: 'All Open', showCompleted: false, dateFilter: 'all' }),
    createWidget('ai-chat', {}),
    createWidget('weekly-review', { showGoals: true, showReflections: true }),
    createWidget('calendar-full', { defaultView: 'week', showTaskCounts: true }),
    createWidget('habit-tracker', {}),
  ];

  const weekWindow: Window = {
    id: crypto.randomUUID(),
    name: 'Week',
    icon: 'calendar',
    widgets: weekWidgets,
    layouts: [
      { i: weekWidgets[0].id, x: 0, y: 0, w: 4, h: 7, minW: 2, minH: 3 },
      { i: weekWidgets[1].id, x: 4, y: 0, w: 4, h: 7, minW: 2, minH: 3 },
      { i: weekWidgets[2].id, x: 8, y: 0, w: 4, h: 7, minW: 2, minH: 3 },
      { i: weekWidgets[3].id, x: 0, y: 7, w: 4, h: 6, minW: 2, minH: 3 },
      { i: weekWidgets[4].id, x: 4, y: 7, w: 4, h: 6, minW: 3, minH: 3 },
      { i: weekWidgets[5].id, x: 8, y: 7, w: 4, h: 6, minW: 3, minH: 3 },
    ],
    createdAt: now,
    updatedAt: now,
  };

  // ── Habits & Goals Window ────────────────────────────────────────────
  // Top row: habits + goals (the stars of this window)
  // Bottom row: AI chat spanning full width
  const habitsWidgets: Widget[] = [
    createWidget('habit-tracker', { showWeekGrid: true, showStreaks: true }),
    createWidget('goals', { showAchieved: false, showLinkedItems: true }),
    createWidget('ai-chat', {}),
  ];

  const habitsWindow: Window = {
    id: crypto.randomUUID(),
    name: 'Habits & Goals',
    icon: 'target',
    widgets: habitsWidgets,
    layouts: [
      { i: habitsWidgets[0].id, x: 0, y: 0, w: 6, h: 7, minW: 3, minH: 4 },
      { i: habitsWidgets[1].id, x: 6, y: 0, w: 6, h: 7, minW: 3, minH: 4 },
      { i: habitsWidgets[2].id, x: 0, y: 7, w: 12, h: 5, minW: 2, minH: 3 },
    ],
    createdAt: now,
    updatedAt: now,
  };

  return {
    windows: [dayWindow, weekWindow, habitsWindow],
    activeWindowId: dayWindow.id,
    defaultWindowId: dayWindow.id,
  };
}

// ============================================================================
// State & Actions
// ============================================================================

type Action =
  | { type: 'LOAD_CONFIG'; payload: WindowsConfig }
  | { type: 'SET_ACTIVE_WINDOW'; payload: string }
  | { type: 'CREATE_WINDOW'; payload: { name: string; widgets?: Widget[] } }
  | { type: 'UPDATE_WINDOW'; payload: { windowId: string; updates: Partial<Pick<Window, 'name' | 'icon'>> } }
  | { type: 'DELETE_WINDOW'; payload: string }
  | { type: 'ADD_WIDGET'; payload: { windowId: string; widget: Widget } }
  | { type: 'UPDATE_WIDGET'; payload: { windowId: string; widgetId: string; config: Partial<WidgetConfigMap[WidgetType]> } }
  | { type: 'REMOVE_WIDGET'; payload: { windowId: string; widgetId: string } }
  | { type: 'UPDATE_LAYOUTS'; payload: { windowId: string; layouts: WidgetLayout[] } }
  | { type: 'UPDATE_RESPONSIVE_LAYOUTS'; payload: { windowId: string; allLayouts: Record<string, WidgetLayout[]> } }
  | { type: 'SET_DEFAULT_WINDOW'; payload: string };

function reducer(state: WindowsConfig, action: Action): WindowsConfig {
  switch (action.type) {
    case 'LOAD_CONFIG':
      return action.payload;

    case 'SET_ACTIVE_WINDOW':
      return { ...state, activeWindowId: action.payload };

    case 'CREATE_WINDOW': {
      const newWindow = createWindow(action.payload.name, action.payload.widgets);
      return {
        ...state,
        windows: [...state.windows, newWindow],
        activeWindowId: newWindow.id,
      };
    }

    case 'UPDATE_WINDOW': {
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.payload.windowId
            ? { ...w, ...action.payload.updates, updatedAt: new Date().toISOString() }
            : w
        ),
      };
    }

    case 'DELETE_WINDOW': {
      const filtered = state.windows.filter((w) => w.id !== action.payload);
      const newActive =
        state.activeWindowId === action.payload
          ? filtered[0]?.id ?? null
          : state.activeWindowId;
      return {
        ...state,
        windows: filtered,
        activeWindowId: newActive,
        defaultWindowId:
          state.defaultWindowId === action.payload ? newActive : state.defaultWindowId,
      };
    }

    case 'ADD_WIDGET': {
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.id !== action.payload.windowId) return w;

          const widget = action.payload.widget;
          const defaults = DEFAULT_WIDGET_SIZES[widget.type];

          // Find the next available position
          const maxY = w.layouts.reduce((max, l) => Math.max(max, l.y + l.h), 0);

          const newLayout: WidgetLayout = {
            i: widget.id,
            x: 0,
            y: maxY,
            ...defaults,
          };

          return {
            ...w,
            widgets: [...w.widgets, widget],
            layouts: [...w.layouts, newLayout],
            updatedAt: new Date().toISOString(),
          };
        }),
      };
    }

    case 'UPDATE_WIDGET': {
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.id !== action.payload.windowId) return w;
          return {
            ...w,
            widgets: w.widgets.map((widget) =>
              widget.id === action.payload.widgetId
                ? { ...widget, config: { ...widget.config, ...action.payload.config } }
                : widget
            ),
            updatedAt: new Date().toISOString(),
          };
        }),
      };
    }

    case 'REMOVE_WIDGET': {
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.id !== action.payload.windowId) return w;
          return {
            ...w,
            widgets: w.widgets.filter((widget) => widget.id !== action.payload.widgetId),
            layouts: w.layouts.filter((l) => l.i !== action.payload.widgetId),
            updatedAt: new Date().toISOString(),
          };
        }),
      };
    }

    case 'UPDATE_LAYOUTS': {
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.payload.windowId
            ? { ...w, layouts: action.payload.layouts, updatedAt: new Date().toISOString() }
            : w
        ),
      };
    }

    case 'UPDATE_RESPONSIVE_LAYOUTS': {
      return {
        ...state,
        windows: state.windows.map((w) => {
          if (w.id !== action.payload.windowId) return w;
          const allLayouts = action.payload.allLayouts;
          // Keep lg layout synced to the primary layouts field for backward compat
          const lgLayout = allLayouts['lg'];
          return {
            ...w,
            layouts: lgLayout ?? w.layouts,
            responsiveLayouts: allLayouts,
            updatedAt: new Date().toISOString(),
          };
        }),
      };
    }

    case 'SET_DEFAULT_WINDOW':
      return { ...state, defaultWindowId: action.payload };

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

export const JOURNAL_WINDOW_ID = '__journal__';
export const FOCUS_WINDOW_ID = '__focus__';
export const ALL_TASKS_WINDOW_ID = '__all_tasks__';
export const ACTIVITY_WINDOW_ID = '__activity__';

interface WindowsContextType {
  config: WindowsConfig;
  activeWindow: Window | null;
  isJournalActive: boolean;
  isFocusActive: boolean;
  isAllTasksActive: boolean;
  isActivityActive: boolean;
  isEditing: boolean;
  setEditing: (editing: boolean) => void;
  deviceType: 'desktop' | 'mobile';
  isMobile: boolean;

  // Home screen
  homeWindowId: string | null;
  setDefaultWindow: (windowId: string) => void;

  // Window operations
  setActiveWindow: (windowId: string) => void;
  createWindow: (name: string, widgets?: Widget[]) => void;
  updateWindow: (windowId: string, updates: Partial<Pick<Window, 'name' | 'icon'>>) => void;
  deleteWindow: (windowId: string) => void;

  // Widget operations
  addWidget: <T extends WidgetType>(windowId: string, type: T, config?: Partial<WidgetConfigMap[T]>) => void;
  updateWidget: (windowId: string, widgetId: string, config: Partial<WidgetConfigMap[WidgetType]>) => void;
  removeWidget: (windowId: string, widgetId: string) => void;

  // Layout operations
  updateLayouts: (windowId: string, layouts: WidgetLayout[]) => void;
  updateResponsiveLayouts: (windowId: string, allLayouts: Record<string, WidgetLayout[]>) => void;

  // Reset
  resetToDefaults: () => void;
}

const WindowsContext = createContext<WindowsContextType | undefined>(undefined);

export function useWindows(): WindowsContextType {
  const context = useContext(WindowsContext);
  if (!context) {
    throw new Error('useWindows must be used within a WindowsProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface WindowsProviderProps {
  children: ReactNode;
}

export function WindowsProvider({ children }: WindowsProviderProps) {
  const [config, dispatch] = useReducer(reducer, null, () => {
    return loadFromStorage() ?? createDefaultConfig();
  });
  const [isEditing, setEditing] = useState(false);
  const deviceType = useReactiveDeviceType();
  const isMobile = deviceType === 'mobile';
  const serverSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDoneRef = useRef(false);

  // Fetch config from server on mount
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      initialLoadDoneRef.current = true;
      return;
    }

    let cancelled = false;
    authService.getWindowsConfig(deviceType).then((serverConfig) => {
      if (cancelled) return;
      initialLoadDoneRef.current = true;
      if (serverConfig && typeof serverConfig === 'object' && 'windows' in serverConfig) {
        dispatch({ type: 'LOAD_CONFIG', payload: sanitizeConfig(serverConfig as unknown as WindowsConfig) });
      }
      // If null: either new user or fetch returned empty. Don't push defaults —
      // they'll be saved on the user's first actual config change.
    }).catch((error) => {
      console.error('Failed to fetch windows config from server:', error);
      // Mark done so subsequent user-driven changes can still save,
      // but we never push defaults automatically on fetch failure.
      initialLoadDoneRef.current = true;
    });
    return () => { cancelled = true; };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage on every change + debounced server save
  useEffect(() => {
    saveToStorage(config);

    // Only save to server after the initial fetch completes.
    // This prevents a race where defaults overwrite the user's real config
    // before the server fetch returns.
    if (authService.isAuthenticated() && initialLoadDoneRef.current) {
      if (serverSaveTimerRef.current) {
        clearTimeout(serverSaveTimerRef.current);
      }
      serverSaveTimerRef.current = setTimeout(() => {
        authService.saveWindowsConfig(config as unknown as Record<string, unknown>, deviceType);
      }, 2000);
    }

    return () => {
      if (serverSaveTimerRef.current) {
        clearTimeout(serverSaveTimerRef.current);
      }
    };
  }, [config, deviceType]);

  const isJournalActive = config.activeWindowId === JOURNAL_WINDOW_ID;
  const isFocusActive = config.activeWindowId === FOCUS_WINDOW_ID;
  const isAllTasksActive = config.activeWindowId === ALL_TASKS_WINDOW_ID;
  const isActivityActive = config.activeWindowId === ACTIVITY_WINDOW_ID;
  const isSpecialWindow = isJournalActive || isFocusActive || isAllTasksActive || isActivityActive;
  const activeWindow = isSpecialWindow
    ? null
    : config.windows.find((w) => w.id === config.activeWindowId) ?? null;

  // Auto-fix stale activeWindowId: if it doesn't match any window or special ID, select the first window
  useEffect(() => {
    if (!isSpecialWindow && !activeWindow && config.windows.length > 0) {
      const fallbackId = config.defaultWindowId && config.windows.some(w => w.id === config.defaultWindowId)
        ? config.defaultWindowId
        : config.windows[0].id;
      dispatch({ type: 'SET_ACTIVE_WINDOW', payload: fallbackId });
    }
  }, [isSpecialWindow, activeWindow, config.windows, config.defaultWindowId]);

  const homeWindowId = useMemo(() => {
    if (config.defaultWindowId && config.windows.some(w => w.id === config.defaultWindowId)) {
      return config.defaultWindowId;
    }
    return config.windows[0]?.id ?? null;
  }, [config.defaultWindowId, config.windows]);

  const setDefaultWindow = useCallback((windowId: string) => {
    dispatch({ type: 'SET_DEFAULT_WINDOW', payload: windowId });
  }, []);

  const setActiveWindow = useCallback((windowId: string) => {
    dispatch({ type: 'SET_ACTIVE_WINDOW', payload: windowId });
  }, []);

  const createWindowAction = useCallback((name: string, widgets?: Widget[]) => {
    dispatch({ type: 'CREATE_WINDOW', payload: { name, widgets } });
  }, []);

  const updateWindow = useCallback(
    (windowId: string, updates: Partial<Pick<Window, 'name' | 'icon'>>) => {
      dispatch({ type: 'UPDATE_WINDOW', payload: { windowId, updates } });
    },
    []
  );

  const deleteWindow = useCallback((windowId: string) => {
    dispatch({ type: 'DELETE_WINDOW', payload: windowId });
  }, []);

  const addWidget = useCallback(
    <T extends WidgetType>(windowId: string, type: T, widgetConfig?: Partial<WidgetConfigMap[T]>) => {
      const widget = createWidget(type, widgetConfig);
      dispatch({ type: 'ADD_WIDGET', payload: { windowId, widget } });
    },
    []
  );

  const updateWidget = useCallback(
    (windowId: string, widgetId: string, widgetConfig: Partial<WidgetConfigMap[WidgetType]>) => {
      dispatch({ type: 'UPDATE_WIDGET', payload: { windowId, widgetId, config: widgetConfig } });
    },
    []
  );

  const removeWidget = useCallback((windowId: string, widgetId: string) => {
    dispatch({ type: 'REMOVE_WIDGET', payload: { windowId, widgetId } });
  }, []);

  const updateLayouts = useCallback((windowId: string, layouts: WidgetLayout[]) => {
    dispatch({ type: 'UPDATE_LAYOUTS', payload: { windowId, layouts } });
  }, []);

  const updateResponsiveLayouts = useCallback((windowId: string, allLayouts: Record<string, WidgetLayout[]>) => {
    dispatch({ type: 'UPDATE_RESPONSIVE_LAYOUTS', payload: { windowId, allLayouts } });
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = createDefaultConfig();
    dispatch({ type: 'LOAD_CONFIG', payload: defaults });
    // Clear server config so defaults are re-saved via the config useEffect
    authService.resetWindowsConfig(deviceType);
  }, [deviceType]);

  const value: WindowsContextType = useMemo(() => ({
    config,
    activeWindow,
    isJournalActive,
    isFocusActive,
    isAllTasksActive,
    isActivityActive,
    isEditing,
    setEditing,
    deviceType,
    isMobile,
    homeWindowId,
    setDefaultWindow,
    setActiveWindow,
    createWindow: createWindowAction,
    updateWindow,
    deleteWindow,
    addWidget,
    updateWidget,
    removeWidget,
    updateLayouts,
    updateResponsiveLayouts,
    resetToDefaults,
  }), [
    config, activeWindow, isJournalActive, isFocusActive, isAllTasksActive, isActivityActive, isEditing, setEditing, deviceType, isMobile,
    homeWindowId, setDefaultWindow, setActiveWindow, createWindowAction, updateWindow, deleteWindow, addWidget,
    updateWidget, removeWidget, updateLayouts, updateResponsiveLayouts, resetToDefaults,
  ]);

  return <WindowsContext.Provider value={value}>{children}</WindowsContext.Provider>;
}
